import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Alert, Divider,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, Grid, Paper, InputAdornment, IconButton, Tooltip,
} from '@mui/material';
import {
  Security, QrCode, Key, ContentCopy, CheckCircle, Warning,
  Terminal, Refresh,
} from '@mui/icons-material';
import apiClient from '../../utils/apiClient';
import { useAppSelector, useAppDispatch } from '../../hooks/useAppHooks';
import { fetchCurrentUser } from '../../features/auth/authSlice';
import { useWebSocket } from '../../hooks/useWebSocket';

const SecuritySettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { robotsOnline } = useWebSocket();

  // 2FA state
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [recoveryCodesRemaining, setRecoveryCodesRemaining] = useState(0);
  const [setupData, setSetupData] = useState<{ qr_code: string; secret: string } | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [showRecoveryDialog, setShowRecoveryDialog] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);

  // SSH state
  const [robots, setRobots] = useState<any[]>([]);
  const [sshLoading, setSshLoading] = useState(false);
  // v6.3.25: per-robot AI module (Jetson) status pulled from the last
  // telemetry snapshot the server has for each robot.
  const [jetsonInfo, setJetsonInfo] = useState<Record<string, any>>({});
  useEffect(() => {
    (async () => {
      try {
        const r = await apiClient.get('/admin/voiceai/jetson-status');
        const map: Record<string, any> = {};
        for (const t of r.data || []) {
          if (t?.jetson) map[t.robot_serial || t.serial] = t.jetson;
        }
        setJetsonInfo(map);
      } catch { /* endpoint optional */ }
    })();
  }, [robotsOnline]);

  useEffect(() => {
    check2FAStatus();
    fetchRobots();
  }, []);

  const check2FAStatus = async () => {
    try {
      const response = await apiClient.get('/auth/2fa/status');
      setTwoFAEnabled(response.data.enabled);
      setRecoveryCodesRemaining(response.data.recovery_codes_remaining);
    } catch {}
  };

  const fetchRobots = async () => {
    try {
      const response = await apiClient.get('/robots');
      setRobots(response.data);
    } catch (e) {
      console.error('Failed to fetch robots for SSH section:', e);
    }
  };

  const handleSetup2FA = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const response = await apiClient.post('/auth/2fa/setup');
      setSetupData(response.data);
    } catch (e: any) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Setup failed' });
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const response = await apiClient.post('/auth/2fa/verify', { code: verifyCode });
      console.log('2FA verify response:', JSON.stringify(response.data));
      
      const codes = response.data?.recovery_codes;
      if (!codes || !Array.isArray(codes) || codes.length === 0) {
        console.error('2FA verify: no recovery codes in response', response.data);
        setMessage({ type: 'error', text: 'Verification succeeded but no recovery codes received. Contact admin.' });
        setTwoFAEnabled(true);
        setSetupData(null);
        setLoading(false);
        return;
      }
      
      console.log('2FA verified, recovery codes count:', codes.length, 'first:', codes[0]);
      
      // Set codes FIRST, then show dialog
      setRecoveryCodes(codes);
      setRecoveryCodesRemaining(codes.length);
      setSetupData(null);
      setVerifyCode('');
      setTwoFAEnabled(true);
      setLoading(false);
      
      // Use requestAnimationFrame to ensure state is committed before opening dialog
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          console.log('Opening recovery dialog');
          setShowRecoveryDialog(true);
        });
      });
      return; // Skip finally block's setLoading
    } catch (e: any) {
      console.error('2FA verify error:', e);
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Verification failed. Check your code and try again.' });
      setLoading(false);
    }
  };
  
  const handleCloseRecoveryDialog = () => {
    setShowRecoveryDialog(false);
    setMessage({ type: 'success', text: '2FA enabled successfully! Recovery codes saved.' });
    dispatch(fetchCurrentUser());
  };

  const handleDisable2FA = async () => {
    setLoading(true);
    try {
      await apiClient.post('/auth/2fa/disable', { code: disableCode });
      setTwoFAEnabled(false);
      setShowDisableDialog(false);
      setDisableCode('');
      setMessage({ type: 'success', text: '2FA has been disabled' });
      dispatch(fetchCurrentUser());
    } catch (e: any) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Failed to disable 2FA' });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedSecret(true);
    setTimeout(() => setCopiedSecret(false), 2000);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Security /> Security Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage two-factor authentication and remote access for your account.
      </Typography>

      {message && <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      {/* ==================== 2FA Section ==================== */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Key color="primary" />
              <Typography variant="h6">Two-Factor Authentication</Typography>
            </Box>
            <Chip
              icon={twoFAEnabled ? <CheckCircle /> : <Warning />}
              label={twoFAEnabled ? 'Enabled' : 'Disabled'}
              color={twoFAEnabled ? 'success' : 'default'}
              size="small"
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add an extra layer of security by requiring a code from your authenticator app when signing in.
          </Typography>

          {!twoFAEnabled && !setupData && (
            <Button variant="contained" startIcon={<QrCode />} onClick={handleSetup2FA} disabled={loading}>
              {loading ? 'Setting up...' : 'Enable 2FA'}
            </Button>
          )}

          {/* Setup flow */}
          {setupData && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.), then enter the 6-digit code below.
              </Alert>
              
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <img src={setupData.qr_code} alt="2FA QR Code" style={{ width: 200, height: 200, borderRadius: 8 }} />
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Manual: <code style={{ fontSize: '0.7rem' }}>{setupData.secret}</code>
                    </Typography>
                    <IconButton size="small" onClick={() => copyToClipboard(setupData.secret)}>
                      <ContentCopy sx={{ fontSize: 12 }} />
                    </IconButton>
                    {copiedSecret && <Typography variant="caption" color="success.main">Copied!</Typography>}
                  </Box>
                </Box>
                
                <Box sx={{ flex: 1, minWidth: 250 }}>
                  <TextField
                    label="Verification Code" value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    fullWidth sx={{ mb: 2 }} placeholder="000000" autoFocus
                    inputProps={{ maxLength: 6, style: { letterSpacing: '0.3em', fontFamily: 'monospace', fontSize: '1.2em' } }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" onClick={handleVerify2FA} disabled={loading || verifyCode.length < 6}>
                      {loading ? <CircularProgress size={20} /> : 'Verify & Enable'}
                    </Button>
                    <Button onClick={() => { setSetupData(null); setVerifyCode(''); }}>Cancel</Button>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {/* Enabled state */}
          {twoFAEnabled && !setupData && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Recovery codes remaining: <strong>{recoveryCodesRemaining}</strong>
              </Typography>
              <Button variant="outlined" color="error" size="small" onClick={() => setShowDisableDialog(true)}>
                Disable 2FA
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ==================== SSH Section (super_admin only) ==================== */}
      {user?.role === 'super_admin' && (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Terminal color="primary" />
            <Typography variant="h6">SSH Remote Access</Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Manage SSH tunnel port assignments for remote robot access.
          </Typography>

          {robots.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No robots registered.</Typography>
          ) : (
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& th, & td': { p: 1, textAlign: 'left', borderBottom: '1px solid', borderColor: 'divider' }, '& th': { fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' } }}>
            <thead>
              <tr>
                <th>Robot</th>
                <th>SSH Port</th>
                <th>Status</th>
                <th>Last Seen</th>
                <th style={{ width: 80 }}>Connect</th>
              </tr>
            </thead>
            <tbody>
              {robots.map((robot: any) => {
                const isOnline = robotsOnline.includes(robot.serial_number);
                const jet = jetsonInfo[robot.serial_number];
                const port = robot.ssh_port;
                const lastSeen = robot.last_seen ? new Date(robot.last_seen) : null;
                const sshCmd = port ? `ssh -p ${port} amit@${window.location.hostname}` : '';
                
                const formatLastSeen = (d: Date | null) => {
                  if (!d) return 'Never';
                  const now = new Date();
                  const diff = Math.floor((now.getTime() - d.getTime()) / 1000);
                  if (diff < 60) return `${diff}s ago`;
                  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
                  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
                  return d.toLocaleDateString();
                };

                return (
                  <tr key={robot.id}>
                    <td>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: isOnline ? 'success.main' : 'text.disabled', flexShrink: 0 }} />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{robot.serial_number}</Typography>
                          {robot.friendly_name && <Typography variant="caption" color="text.secondary">{robot.friendly_name}</Typography>}
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>{robot.serial_number}</Typography>
                          {jet && jet.mode !== false && (() => {
                            // v6.3.26: prefer the port the robot reports
                            // (bridge config jetson_ssh_port); fall back to
                            // the value saved here in robot metadata.
                            const jport = jet.ssh_port || robot.robot_metadata?.jetson_ssh_port;
                            return (
                              <Typography variant="caption" sx={{ display: 'block' }}
                                color={jet.online ? 'success.main' : 'text.disabled'}>
                                AI module: {jet.online ? 'online' : 'offline'}
                                {' · SSH '}
                                <input
                                  defaultValue={jport || ''}
                                  placeholder="port"
                                  style={{ width: 62, fontSize: '0.7rem', padding: '1px 4px' }}
                                  onBlur={async (e) => {
                                    const val = e.target.value.trim();
                                    if (String(jport || '') === val) return;
                                    try {
                                      await apiClient.patch(`/robots/${robot.id}`, {
                                        metadata: {
                                          ...(robot.robot_metadata || {}),
                                          jetson_ssh_port: val ? Number(val) : null,
                                        },
                                      });
                                      setRobots((prev: any[]) => prev.map((r: any) => r.id === robot.id
                                        ? { ...r, robot_metadata: { ...(r.robot_metadata || {}), jetson_ssh_port: val ? Number(val) : null } }
                                        : r));
                                    } catch { /* ignore */ }
                                  }} />
                                {jport ? ` → ssh -p ${jport} amit@${window.location.hostname}` : ''}
                              </Typography>
                            );
                          })()}
                        </Box>
                      </Box>
                    </td>
                    <td>
                      <TextField
                        size="small"
                        type="number"
                        value={port ?? ''}
                        placeholder="e.g. 2220"
                        onChange={(e) => {
                          const val = e.target.value ? parseInt(e.target.value) : null;
                          setRobots((prev: any[]) => prev.map((r: any) => r.id === robot.id ? { ...r, ssh_port: val } : r));
                        }}
                        onBlur={async () => {
                          try {
                            await apiClient.patch(`/robots/${robot.id}`, { ssh_port: port });
                          } catch (e) {
                            console.error('Failed to save SSH port:', e);
                          }
                        }}
                        sx={{ width: 100, '& input': { fontFamily: 'monospace', fontSize: '0.85rem', py: 0.5, px: 1 } }}
                        inputProps={{ min: 1024, max: 65535 }}
                      />
                    </td>
                    <td>
                      <Chip
                        label={isOnline ? 'Online' : 'Offline'}
                        size="small"
                        color={isOnline ? 'success' : 'default'}
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 22 }}
                      />
                    </td>
                    <td>
                      <Typography variant="caption" color="text.secondary">
                        {formatLastSeen(lastSeen)}
                      </Typography>
                    </td>
                    <td>
                      {port ? (
                        <Tooltip title={`Copy: ${sshCmd}`}>
                          <IconButton size="small" onClick={() => copyToClipboard(sshCmd)}>
                            <ContentCopy sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Box>
          )}
        </CardContent>
      </Card>
      )}

      {/* Disable 2FA Dialog */}
      <Dialog open={showDisableDialog} onClose={() => setShowDisableDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter your current authenticator code or a recovery code to disable 2FA.
          </Typography>
          <TextField
            fullWidth label="Code" value={disableCode}
            onChange={(e) => setDisableCode(e.target.value)} autoFocus
            inputProps={{ maxLength: 8 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowDisableDialog(false); setDisableCode(''); }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDisable2FA} disabled={loading || !disableCode}>
            {loading ? <CircularProgress size={20} /> : 'Disable 2FA'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recovery Codes Dialog */}
      <Dialog open={showRecoveryDialog} onClose={handleCloseRecoveryDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle color="success" /> 2FA Enabled Successfully
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Save these recovery codes in a safe place. Each code can only be used once. If you lose your authenticator app, these are the only way to access your account.
          </Alert>
          <Paper sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.2)', fontFamily: 'monospace', textAlign: 'center' }}>
            <Grid container spacing={1}>
              {recoveryCodes.map((code, i) => (
                <Grid item xs={6} key={i}>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', letterSpacing: '0.1em' }}>
                    {code}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Paper>
          <Button
            fullWidth sx={{ mt: 2 }} variant="outlined"
            startIcon={<ContentCopy />}
            onClick={() => copyToClipboard(recoveryCodes.join('\n'))}
          >
            Copy All Codes
          </Button>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleCloseRecoveryDialog}>
            I've saved my codes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SecuritySettings;
